import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  REQUEST_TYPE_MAP,
  approveRejectMedicalAttendance,
  fetchDocumentTypes,
  fetchHospitals,
  fetchRelationships,
  findOneMedicalAttendance,
  formatSapDate,
  formatSapDateTime,
  getDocuments,
  getLoggedInEmpId,
  getLoggedInUser,
  getRemarksList,
  getStatusBadgeClass,
  getStatusLabel,
  normalizeDobToDMY,
  resolveDocType,
  uploadMedicalAttendanceFile,
} from "../otherServices/medicalAttendance/medicalAttendance.service";

const NO_DATA = "No data found";

const MedicalAttendanceApproval: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { requestID } = useParams<{ requestID: string }>();
  const user = getLoggedInUser();
  const approverEmpId = getLoggedInEmpId();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hospitalLabel, setHospitalLabel] = useState("");
  const [diseaseLabel, setDiseaseLabel] = useState("");
  const [docTypeMap, setDocTypeMap] = useState<Record<string, string>>({});
  const [ivfPatientName, setIvfPatientName] = useState("");
  const [patientNamesForRelation, setPatientNamesForRelation] = useState<any[]>([]);
  const [otherDocs, setOtherDocs] = useState<any[]>([]);
  const [docLoading, setDocLoading] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<"A" | "R" | null>(null);
  const [remarkText, setRemarkText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refId = requestID ? decodeURIComponent(requestID) : "";

  useEffect(() => {
    fetchDocumentTypes().then(setDocTypeMap).catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneMedicalAttendance(refId);
        setDetail(data);

        const key = data?.HospitalRecordKey;
        const requestType = data?.RequestTypeForMedicalAttendanceRules;
        const requestTypeStr = requestType != null ? String(requestType) : "";

        if (requestTypeStr === "62") {
          setDiseaseLabel(
            data?.DiseaseName ||
              data?.MappedDiseasePara62 ||
              (data?.DiseaseForProlongTreatmentPara62 != null
                ? String(data.DiseaseForProlongTreatmentPara62)
                : "")
          );
        } else if (requestTypeStr === "63") {
          setDiseaseLabel(
            data?.DiseaseName ||
              (data?.DiseaseForProlongTreatmentPara63 != null
                ? String(data.DiseaseForProlongTreatmentPara63)
                : "")
          );
        } else {
          setDiseaseLabel("");
        }

        if (requestTypeStr === "62") {
          const name = data?.HospitalName ? String(data.HospitalName).trim() : "";
          const address = data?.HospitalAddress ? String(data.HospitalAddress).trim() : "";
          if (name && address) setHospitalLabel(`${name} - ${address}`);
          else setHospitalLabel(name || address || (key != null ? String(key) : ""));
        } else if (key && requestType) {
          try {
            const options = await fetchHospitals(String(requestType));
            const opt = options.find((d) => String(d.id) === String(key));
            setHospitalLabel(opt?.label || String(key));
          } catch {
            setHospitalLabel(String(key));
          }
        }

        if (requestTypeStr === "66" || requestTypeStr === "62" || requestTypeStr === "63") {
          const empId4Patient = String(data?.EmpID ?? "").trim();
          if (empId4Patient) {
            try {
              setPatientNamesForRelation(await fetchRelationships(empId4Patient));
            } catch {
              /* ignore */
            }
          }
          const ownerEmpId = String(data?.EmpID ?? "").trim().replace(/^0+/, "");
          const viewerEmpId = String(approverEmpId).replace(/^0+/, "");
          if (ownerEmpId && viewerEmpId && ownerEmpId === viewerEmpId) {
            const empFullName = `${user?.details?.firstname || ""} ${
              user?.details?.lsastname ||
              user?.details?.secondname ||
              user?.details?.lastname ||
              ""
            }`.trim();
            if (empFullName) setIvfPatientName(empFullName);
          }
        }
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  const requestType =
    detail?.RequestTypeForMedicalAttendanceRules != null
      ? String(detail.RequestTypeForMedicalAttendanceRules)
      : "";
  const isIVF = requestType === "66";
  const isPara62 = requestType === "62";
  const isPara63 = requestType === "63";
  const requestTypeLabel = requestType ? REQUEST_TYPE_MAP[requestType] ?? requestType : NO_DATA;
  const createdDate = detail?.created_at
    ? formatSapDate(detail.created_at).replace(/-/g, "/")
    : NO_DATA;

  const applicant: any = detail?.applicant_details;
  const applicantFullName = applicant
    ? `${applicant?.firstname || ""} ${
        applicant?.lsastname || applicant?.secondname || applicant?.lastname || ""
      }`.trim()
    : "";
  const employeeName =
    detail?.EmployeeName || detail?.EmpName || detail?.Name || applicantFullName || NO_DATA;
  const employeeDesignation =
    detail?.Designation ||
    applicant?.designation_details?.longText ||
    applicant?.sap_department_designation_wings?.designation_name ||
    NO_DATA;
  const employeeDepartment =
    detail?.Department ||
    applicant?.sap_department_designation_wings?.department_name ||
    NO_DATA;
  const employeePayscale =
    detail?.PayScale || detail?.Payscale || detail?.pay_scale || applicant?.pay_scale || NO_DATA;
  const employeeModeOfRecruitment =
    detail?.EmploymentType ||
    detail?.employmentType ||
    detail?.ModeOfRecruitment ||
    applicant?.employmentType ||
    applicant?.EmploymentType ||
    applicant?.ModeOfRecruitment ||
    NO_DATA;
  const employeeEmploymentStatus =
    detail?.EmpStatus ||
    detail?.empStatus ||
    detail?.EmploymentStatus ||
    applicant?.empStatus ||
    applicant?.EmpStatus ||
    applicant?.EmploymentStatus ||
    NO_DATA;
  const employeePhone =
    detail?.ContactNumber != null && String(detail.ContactNumber).trim() !== ""
      ? String(detail.ContactNumber).trim()
      : applicant?.phone != null && String(applicant.phone).trim() !== ""
        ? String(applicant.phone).trim()
        : NO_DATA;
  const employeeEmail =
    detail?.EmailID != null && String(detail.EmailID).trim() !== ""
      ? String(detail.EmailID).trim()
      : applicant?.email != null && String(applicant.email).trim() !== ""
        ? String(applicant.email).trim()
        : NO_DATA;

  const displayHospitalFull =
    hospitalLabel ||
    (detail?.HospitalRecordKey != null ? String(detail.HospitalRecordKey) : "") ||
    "-";
  const displayHospitalName =
    !displayHospitalFull || displayHospitalFull === "-"
      ? NO_DATA
      : displayHospitalFull.includes(" - ")
        ? displayHospitalFull.split(" - ")[0].trim() || displayHospitalFull
        : displayHospitalFull;
  const displayHospitalAddress =
    hospitalLabel && hospitalLabel.includes(" - ")
      ? hospitalLabel.split(" - ").slice(1).join(" - ").trim() || NO_DATA
      : NO_DATA;
  const displayDisease = detail?.DiseaseName || diseaseLabel || NO_DATA;

  const selfPatientName = String(ivfPatientName ?? "").trim();
  const ownerEmpIdNorm = String(detail?.EmpID ?? "").trim().replace(/^0+/, "");
  const viewerEmpIdNorm = String(approverEmpId).replace(/^0+/, "");
  const isOwnRequest =
    ownerEmpIdNorm !== "" && viewerEmpIdNorm !== "" && ownerEmpIdNorm === viewerEmpIdNorm;
  const isSelfPatient = (() => {
    const rawRel = String(detail?.RelationToEmployee ?? "").trim().toUpperCase();
    if (rawRel === "SELF" || rawRel === "0") return true;
    const dpn = String(detail?.PatientName ?? "").trim().toLowerCase();
    if (dpn === "self") return true;
    const spn = selfPatientName.toLowerCase();
    if (spn && dpn && dpn === spn) {
      return rawRel === "" || rawRel === "SELF" || rawRel === "0";
    }
    if (isOwnRequest && (rawRel === "" || rawRel === "SELF" || rawRel === "0")) return true;
    return false;
  })();
  const displayRelationToEmployee = (() => {
    if (isSelfPatient) return "Self";
    const rawRelation = String(detail?.RelationToEmployee ?? "").trim();
    if (rawRelation === "") return NO_DATA;
    const rawUpper = rawRelation.toUpperCase();
    if (rawUpper === "SELF" || rawRelation === "0") return "Self";
    const match = patientNamesForRelation.find(
      (p: any) => String(p?.Value ?? "").toUpperCase() === rawUpper
    );
    if (match) {
      const matchVal = String(match.Value ?? "").toUpperCase();
      if (matchVal === "SELF" || String(match.Value ?? "") === "0") return "Self";
      const dd = String(match.Ddtext ?? match.DdText ?? match.ddtext ?? "").trim();
      if (dd) return dd;
    }
    return rawRelation || NO_DATA;
  })();

  const ivfForValue = isSelfPatient ? "SELF" : "SPOUSE";
  const getIVFForLabel = (value?: string) => {
    if (value === "SELF") return t("self") || "Self";
    if (value === "SPOUSE") return t("spouse") || "Spouse";
    return value || NO_DATA;
  };

  const documents = getDocuments(detail);
  const remarksList = getRemarksList(detail);
  const govHospitalName = String(detail?.HospitalName ?? "").trim();
  const govHospitalAddress = String(detail?.HospitalAddress ?? "").trim();
  const isGovernmentHospitalCase =
    (displayHospitalName || "").toLowerCase().includes("government hospital") ||
    !!govHospitalName ||
    !!govHospitalAddress;
  const empDobRaw =
    detail?.EmployeeDOB ||
    detail?.EmpDOB ||
    detail?.PatientDOB ||
    detail?.PatientDob ||
    detail?.DateOfBirth ||
    detail?.DOB ||
    detail?.birthdate ||
    detail?.dob;
  const empDobValue = empDobRaw ? normalizeDobToDMY(empDobRaw) : "";
  const spouseDobRaw = detail?.SpouseDOB || detail?.SpouseDob || detail?.spouseDob;
  const spouseDobValue = spouseDobRaw ? normalizeDobToDMY(spouseDobRaw) : "";
  const spouseNameValue = String(
    detail?.SpouseName || (ivfForValue === "SPOUSE" ? detail?.PatientName : "") || ""
  ).trim();

  const handleFileChoose = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setDocLoading(true);
    try {
      const uploaded = await uploadMedicalAttendanceFile(files[0]);
      setOtherDocs((prev) => [...prev, uploaded]);
      toast.success(t("file_uploaded") || "File uploaded");
    } catch (err: any) {
      toast.error(err?.message || Api.getErrorMessage(err) || "Upload failed");
    } finally {
      setDocLoading(false);
    }
  };

  const openAction = (action: "A" | "R") => {
    if (!detail) return;
    setPendingAction(action);
    setRemarkText("");
    setShowRemarkModal(true);
  };

  const confirmAction = async () => {
    if (!pendingAction || !detail) return;
    if (pendingAction === "R" && !remarkText.trim()) {
      toast.error(t("enter_remark") || "Please enter a remark");
      return;
    }
    setSubmitting(true);
    try {
      const rt = String(detail?.RequestTypeForMedicalAttendanceRules ?? "");
      const toDocument =
        pendingAction === "A"
          ? otherDocs.map((doc) => ({
              FileName: doc.serverKey,
              FileExtension: doc.fileExtension || "application/pdf",
              TypeOfDocument: "05",
            }))
          : [];
      const payload: any = {
        Action: pendingAction,
        ReferenceNumber: String(detail?.ReferenceNumber ?? ""),
        EmpID: String(detail?.EmpID ?? ""),
        ApproverUserID: approverEmpId,
        HRemarks: remarkText.trim(),
        ToDocument: toDocument,
      };
      if (rt === "63") {
        payload.RequestTypeForMedicalAttendanceRules = "63";
        payload.DateOfTreatmentBegin = detail?.DateOfTreatmentBegin ?? "";
        payload.DateOfTreatmentEnd = detail?.DateOfTreatmentEnd ?? "";
      }
      if (rt === "66") {
        payload.RequestTypeForMedicalAttendanceRules = "66";
      }
      const res: any = await approveRejectMedicalAttendance(payload);
      if (res?.success === false) {
        toast.error(
          res?.message ||
            (pendingAction === "A" ? "Approval failed" : "Rejection failed")
        );
        return;
      }
      toast.success(
        res?.message ||
          (pendingAction === "A" ? "Request approved successfully" : "Request rejected successfully")
      );
      setShowRemarkModal(false);
      navigate(routes.todoWorklist.tasks);
    } catch (err: any) {
      toast.error(
        Api.getErrorMessage(err) ||
          (pendingAction === "A" ? "Approval failed" : "Rejection failed")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("medical_attendance_approval")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.todoWorklist.tasks}>Universal Worklist</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("medical_attendance_approval")}
                </li>
              </ol>
            </nav>
          </div>
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left me-1"></i>
            {t("close")}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
            <p className="mt-2 text-muted">Loading request details...</p>
          </div>
        ) : !detail ? (
          <div className="alert alert-light border">{t("no_details_available") || NO_DATA}</div>
        ) : (
          <>
            <div className="alert alert-secondary d-flex align-items-center justify-content-between">
              <span className="fw-semibold">{t("request_status")}</span>
              <span className={getStatusBadgeClass(detail?.RequestStatus)}>
                {getStatusLabel(detail?.RequestStatus, t)}
              </span>
            </div>

            <Section title={t("request_information")} icon="ti ti-briefcase">
              <Field label={t("reference_no")} value={detail?.ReferenceNumber || NO_DATA} />
              <Field label={t("request_type")} value={requestTypeLabel} />
              <Field label={t("created_at")} value={createdDate} />
              <Field label={t("employee_id")} value={detail?.EmpID || NO_DATA} />
            </Section>

            <Section title={t("employee_details")} icon="ti ti-user">
              <Field label={t("name")} value={employeeName} />
              <Field label={t("designation")} value={employeeDesignation} />
              <Field label={t("department")} value={employeeDepartment} />
              <Field label={t("payscale")} value={employeePayscale} />
              <Field label={t("mode_of_recruitment")} value={employeeModeOfRecruitment} />
              <Field label={t("employment_status")} value={employeeEmploymentStatus} />
              <Field label={t("phone_number")} value={employeePhone} />
              <Field label={t("email_id")} value={employeeEmail} />
            </Section>

            <Section title={t("treatment_details")} icon="ti ti-stethoscope">
              {isIVF && (
                <>
                  <Field label={t("IVF_Treatment_For")} value={getIVFForLabel(ivfForValue)} />
                  <Field
                    label={t("marital_status")}
                    value={detail?.MaritalStatus === "M" ? t("married") || "Married" : detail?.MaritalStatus || NO_DATA}
                  />
                  <Field label={t("from_hospital")} value={displayHospitalName} />
                  {isGovernmentHospitalCase && (
                    <>
                      <Field label={t("hospital_name")} value={govHospitalName || NO_DATA} />
                      <Field label={t("hospital_address")} value={govHospitalAddress || displayHospitalAddress} />
                    </>
                  )}
                  {ivfForValue === "SELF" && (
                    <Field label={t("dob_employee")} value={empDobValue || NO_DATA} />
                  )}
                  {ivfForValue === "SPOUSE" && (
                    <>
                      <Field label={t("spouse_name")} value={spouseNameValue || NO_DATA} />
                      <Field label={t("spouse_dob")} value={spouseDobValue || NO_DATA} />
                    </>
                  )}
                </>
              )}
              {(isPara62 || isPara63) && (
                <>
                  <Field label={t("patient_name")} value={detail?.PatientName || NO_DATA} />
                  <Field label={t("relation_to_employee")} value={displayRelationToEmployee} />
                  <Field label={t("patient_age")} value={detail?.Age || NO_DATA} />
                  <Field label={t("disease")} value={displayDisease} />
                  <Field label={t("hospital_name")} value={displayHospitalName} />
                  {isPara62 && <Field label={t("hospital_address")} value={displayHospitalAddress} />}
                </>
              )}
              {isPara63 && (
                <>
                  <Field
                    label={t("Treatment_From")}
                    value={
                      detail?.EmpDateOfTreatmentBegin || detail?.DateOfTreatmentBegin
                        ? normalizeDobToDMY(detail.EmpDateOfTreatmentBegin || detail.DateOfTreatmentBegin)
                        : NO_DATA
                    }
                  />
                  <Field
                    label={t("Treatment_Till")}
                    value={
                      detail?.EmpDateOfTreatmentEnd || detail?.DateOfTreatmentEnd
                        ? normalizeDobToDMY(detail.EmpDateOfTreatmentEnd || detail.DateOfTreatmentEnd)
                        : NO_DATA
                    }
                  />
                </>
              )}
            </Section>

            {isIVF && (
              <div className="alert alert-warning">
                <strong>{t("mandatory_attachments")}:</strong>
                <div>{t("prescription_failure_conventional")}</div>
              </div>
            )}

            {documents.length > 0 && (
              <Section title={t("attachedFiles") || "Attached Files"} icon="ti ti-paperclip">
                {documents.map((doc: any, index: number) => {
                  const url = doc?.url || doc?.URL || doc?.fileUrl;
                  const fileName = doc.FileName || doc.fileName || `Document_${index + 1}.pdf`;
                  return (
                    <div key={doc.id || index} className="border rounded p-2 mb-2">
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="fw-semibold">
                          <i className="ti ti-file me-1"></i>
                          {fileName}
                        </a>
                      ) : (
                        <span className="fw-semibold">
                          <i className="ti ti-file me-1"></i>
                          {fileName}
                        </span>
                      )}
                      <Field
                        label={t("document_type")}
                        value={resolveDocType(doc?.TypeOfDocument, docTypeMap)}
                      />
                    </div>
                  );
                })}
              </Section>
            )}

            <Section title={t("other_documents_optional") || "Other documents (optional)"} icon="ti ti-upload">
              <input
                type="file"
                className="form-control"
                accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
                disabled={docLoading}
                onChange={(e) => {
                  handleFileChoose(e.target.files);
                  e.target.value = "";
                }}
              />
              <small className="text-muted d-block mt-1">
                {t("choose_file_mb") || "PDF (max 2 MB) or JPG/JPEG"}
              </small>
              {otherDocs.length > 0 && (
                <ul className="list-group mt-2">
                  {otherDocs.map((doc, i) => (
                    <li
                      className="list-group-item d-flex justify-content-between align-items-center"
                      key={`${doc.serverKey}-${i}`}
                    >
                      <span>
                        {doc.fileName} <small className="text-muted">({doc.fileSize})</small>
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setOtherDocs((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        <i className="ti ti-trash"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={t("History")} icon="ti ti-history">
              {remarksList.length > 0 ? (
                remarksList.map((remark: any, index: number) => (
                  <div
                    key={index}
                    className={`py-2 ${index < remarksList.length - 1 ? "border-bottom" : ""}`}
                  >
                    <div className="fw-semibold">
                      {remark?.ApproverName || NO_DATA}
                      {(remark?.ApproverUserID || remark?.ApproverLevel) && (
                        <small className="text-muted ms-2">
                          ({remark?.ApproverUserID || remark?.ApproverLevel})
                        </small>
                      )}
                    </div>
                    <div className="small text-muted">{formatSapDateTime(remark?.TimeStamp)}</div>
                    <div>{remark?.HRemarks?.trim() || NO_DATA}</div>
                  </div>
                ))
              ) : (
                <div className="text-muted">{NO_DATA}</div>
              )}
            </Section>

            {isIVF && (
              <Section title={t("declarations_title")} icon="ti ti-checkbox">
                {[t("declaration1"), t("declaration2"), t("declaration3"), t("declaration4")].map(
                  (line, i) => (
                    <p key={i} className="mb-1">
                      {i + 1}. {line}
                    </p>
                  )
                )}
              </Section>
            )}

            <div className="d-flex justify-content-end gap-2 mb-4">
              <button
                type="button"
                className="btn btn-danger"
                disabled={submitting}
                onClick={() => openAction("R")}
              >
                {t("reject")}
              </button>
              <button
                type="button"
                className="btn btn-success"
                disabled={submitting}
                onClick={() => openAction("A")}
              >
                {t("approve")}
              </button>
            </div>
          </>
        )}

        {showRemarkModal && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {pendingAction === "A"
                      ? t("approve_request") || "Approve Request"
                      : t("reject_request") || "Reject Request"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowRemarkModal(false);
                      setPendingAction(null);
                      setRemarkText("");
                    }}
                  />
                </div>
                <div className="modal-body">
                  <p className="text-muted">
                    {pendingAction === "A"
                      ? t("approval_remark_msg") || "Add an optional remark for approval."
                      : t("rejection_remark_msg") || "Please enter a reason for rejection."}
                  </p>
                  <label className="form-label fw-semibold">
                    {t("remarks")}
                    {pendingAction === "R" && <span className="text-danger"> *</span>}
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    maxLength={500}
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    placeholder={
                      pendingAction === "A"
                        ? t("enter_approval_remark") || "Enter approval remark"
                        : t("enter_rejection_reason") || "Enter rejection reason"
                    }
                  />
                  <div className="text-end small text-muted mt-1">{remarkText.length}/500</div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    disabled={submitting}
                    onClick={() => {
                      setShowRemarkModal(false);
                      setPendingAction(null);
                      setRemarkText("");
                    }}
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="button"
                    className={`btn ${pendingAction === "A" ? "btn-success" : "btn-danger"}`}
                    disabled={submitting}
                    onClick={confirmAction}
                  >
                    {submitting ? (
                      <span className="spinner-border spinner-border-sm me-1" />
                    ) : null}
                    {pendingAction === "A" ? t("approve") : t("reject")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">
        <i className={`${icon} me-2`}></i>
        {title}
      </h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default MedicalAttendanceApproval;
